/* eslint-disable no-nested-ternary,import/no-unresolved */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router-dom';
import { isBrowser, isMobile } from 'react-device-detect';
import BigNumber from 'bignumber.js';
import { RATES_QUERY_KEY, USER_PREFERENCES } from '../../../utilities/sharedQueries';
import { getTotalBalance } from '../../Accounts/partials/helpers';
import { showAlert } from '../../../utilities/notifications';
import { CARD_CRYPTODRAFT, CARD_LINKED_WALLETS } from '../queries';
import Wallets from './partials/Wallets';
import LinkWalletsModal from '../../../modules/modals/LinkWalletsModal';
import WalletsWithdrawDepositModal from '../../Accounts/modals/WalletsWithdrawDepositModal';
import AddAccount from '../../Accounts/partials/AddAccount';
import Balance from '../../Accounts/partials/Balance';
import { useLedgerAccounts } from '../../../utilities/sharedHooks/ledgerSDKHooks';
import {
  useCardBalances,
  useCardStatus,
  useUserCardProvider,
  useUserPersonalInfo,
} from '../../../utilities/sharedHooks/user';
import { currencies } from '../../../utilities/constants';
import { useRate } from '../../../utilities/sharedHooks/rates';
import {
  cardProviders,
  CardState,
  cryptodraftScreens,
  resolveCardFiatCurrency,
} from '../constants';
import { BANK_REQUESTS_COINS } from '../../Accounts/constants';
import { FLOWS, sendStats } from '../../../services/mixpanel';
import { MXP_ACCOUNT_FUND } from '../../Accounts/mixpanel-events';
import { SkeletonPageLoader } from '../../../components';

const CardAccounts = () => {
  const { t: translate } = useTranslation();
  const history = useHistory();
  const [totalChangeInFiatValue, setTotalChangeInFiatValue] = useState('0.00');
  const [showLinkWalletsModal, setShowLinkWalletsModal] = useState(false);
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState({});
  const [selectedLedgerAccount, setSelectedLedgerAccount] = useState(null);

  const {
    data: cardInfo,
    refetch: refetchCardInfo,
    isLoading: isLoadingCardInfo,
  } = useCardBalances({
    enabled: false,
  });
  const { data: cardCryptodraftDetails } = useQuery(CARD_CRYPTODRAFT);

  const [ledgerAccounts] = useLedgerAccounts();

  const { cardProvider, isLoading: isLoadingCardProvider } = useUserCardProvider();

  const { data: cardStatus, isLoading: isLoadingCardStatus } = useCardStatus();

  const { isUS } = useUserPersonalInfo();

  const isDepositHidden = process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN && !isUS;

  const isUSVersion = useMemo(() => process.env.REACT_APP_MINIMIZED_US_VERSION === 'true', []);

  useEffect(() => {
    if (
      cardStatus &&
      ![CardState.ACTIVE, CardState.DAMAGED, CardState.ACTIVATION].includes(
        cardStatus?.cardState?.toUpperCase()
      )
    ) {
      history.push('/dashboard/card/details');
    }
  }, [cardStatus, history]);

  const fiatCurrencyCode = useMemo(
    () => resolveCardFiatCurrency(cardProvider, cardStatus, cardInfo),
    [cardProvider, cardStatus, cardInfo]
  );

  const { data: rates, isLoading: isLoadingRates } = useQuery(RATES_QUERY_KEY, {
    onSettled(_, error) {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  const {
    data: wallets,
    refetch: getLinkedWallet,
    isLoading: isLoadingLinkedWallets,
  } = useQuery(CARD_LINKED_WALLETS, {
    onSettled(_, error) {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      }
    },
  });

  useEffect(() => {
    refetchCardInfo();
    getLinkedWallet();
  }, []);

  useEffect(() => {
    sendStats(MXP_ACCOUNT_FUND, {}, FLOWS.ACCOUNTS);
  }, []);

  useEffect(() => {
    const { totalChangeInFiat } = getTotalBalance(fiatCurrencyCode, wallets, rates);
    setTotalChangeInFiatValue(totalChangeInFiat);
  }, [fiatCurrencyCode, rates, wallets]);

  // this value just for accurate calculations when trying to get specific rate based on Optimus display currency
  const principalCurrency = useMemo(() => {
    if (cardStatus?.cardCurrency?.toLowerCase() === BANK_REQUESTS_COINS.EUR) {
      return BANK_REQUESTS_COINS.EUROC;
    }
    if (cardStatus?.cardCurrency?.toLowerCase() === BANK_REQUESTS_COINS.USD) {
      return BANK_REQUESTS_COINS.USDT;
    }
    return cardStatus?.cardCurrency;
  }, [cardStatus?.cardCurrency, cardProvider]);

  const displayCurrencyRate = useRate(cardInfo?.currency, principalCurrency);

  const { data: userPref } = useQuery(USER_PREFERENCES);
  const userDisplayCurrency = userPref?.displayCurrency || 'EUR';

  const cardBalance = useMemo(() => {
    if (cardProvider && cardInfo && wallets && rates && displayCurrencyRate) {
      let onCardBalance = 0.0;
      if (cardProvider === cardProviders.MONAVATE) {
        onCardBalance =
          cardInfo?.availableBalance?.amount > 0 ? cardInfo?.availableBalance?.amount : 0.0;
      } else {
        onCardBalance = cardInfo?.balance;
      }

      let linkedWalletBalance = 0;
      wallets.forEach((wallet) => {
        let cardCurrency;

        switch (cardProvider) {
          case cardProviders.MONAVATE:
            cardCurrency = cardStatus?.cardCurrency;
            break;

          default:
            cardCurrency = cardStatus?.currency?.toUpperCase() || 'USD';
        }

        let rate = 1;
        if (wallet?.coin.toUpperCase() !== cardCurrency?.toUpperCase()) {
          rate =
            BigNumber(rates[`${wallet?.coin?.toUpperCase()}${cardCurrency?.toUpperCase()}`]?.ask) ||
            0.0;
        }

        if (!wallet?.is_cryptodraft) {
          linkedWalletBalance +=
            wallet?.available_balance * rate * currencies[wallet?.coin]?.conversion;
        } else {
          let cryptodraftAvaiableBalance =
            wallet?.available_balance * currencies[wallet?.coin]?.conversion;
          if (cardProvider === cardProviders.MONAVATE) {
            let cryptodraftToCardCurrencyRate = 1;
            if (rates[`${wallet?.coin?.toUpperCase()}${cardCurrency?.toUpperCase()}`]?.ask) {
              cryptodraftToCardCurrencyRate = BigNumber(
                rates[`${wallet?.coin?.toUpperCase()}${cardCurrency?.toUpperCase()}`]?.ask
              );
            }
            cryptodraftAvaiableBalance *= cryptodraftToCardCurrencyRate;
          }

          linkedWalletBalance += cryptodraftAvaiableBalance > 0 ? cryptodraftAvaiableBalance : 0;
        }
      });

      return linkedWalletBalance + onCardBalance;
    }
    return 0;
  }, [
    cardInfo,
    userDisplayCurrency,
    wallets,
    rates,
    cardCryptodraftDetails,
    cardProvider,
    displayCurrencyRate,
  ]);

  const onAddFundsClick = (wallet, ledgerAccount) => {
    if (wallet && wallet?.is_cryptodraft) {
      history.push('/dashboard/card/cryptodraft', {
        initScreen: cryptodraftScreens.CALCULATOR,
        openExtendFromAccountScreen: true,
      });
    } else {
      setShowDepositModal(true);
      setSelectedWallet(wallet);
      setSelectedLedgerAccount(ledgerAccount);
    }
  };

  const renderAddAccount = () => (
    <AddAccount
      icon="icon-wallet"
      label={translate('connect_your_accounts_label')}
      subLabel={translate('manage_your_funding_source_sublabel')}
      disabled={false}
      onClick={() => {
        setShowLinkWalletsModal(true);
      }}
      isConnectYourAccount
    />
  );

  const renderHeader = () => (
    <div className="d-flex">
      <div className="d-flex flex-row w-100 justify-content-between align-items-center mb-4">
        <div className={`row ${isMobile && 'mx-0 px-0'} w-100`}>
          <div className={`col-lg-3 col-md-4 col-sm-4 ${!isMobile && 'ps-0'}`}>
            <div
              className={`${
                !isMobile ? 'd-flex flex-row' : ''
              } cursor-pointer text-hover position-relative`}
              onClick={() => history.push('/dashboard/card/details')}
              onKeyDown={() => history.push('/dashboard/card/details')}
              role="presentation"
              tabIndex="-1">
              <i className="icon icon-arrow-back-navigation text-f4 me-2" aria-label="Back" />
              <b
                className={`text-f5 ${
                  isMobile
                    ? 'position-absolute top-50 start-50 translate-middle'
                    : 'd-block me-auto'
                }`}>
                {translate('connect_accounts_to_card')}
              </b>
            </div>
          </div>
          <div className="col-lg-9 col-md-8 col-sm-8 d-lg-block d-md-block d-none">
            <p className="text-f5 text-dark d-sm-none  d-md-block mx-md-4 mx-sm-0">
              {isUSVersion
                ? translate('connect_account_select_order_note_us')
                : translate('connect_account_select_order_note')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  const isLoadingPage =
    isLoadingRates ||
    isLoadingLinkedWallets ||
    isLoadingCardInfo ||
    isLoadingCardProvider ||
    !displayCurrencyRate ||
    isLoadingCardStatus;

  if (isLoadingPage) {
    return <SkeletonPageLoader />;
  }

  return (
    <div className="container mt-5 p-0 w-100">
      {renderHeader()}
      <div className={`row ${isMobile ? 'px-0 mx-0' : ''}`}>
        <div className={`col-lg-3 col-md-4 col-12 ${!isMobile ? 'ps-0' : 'px-0'}`}>
          <div className={`row ${isMobile && 'mx-0 px-3'}`}>
            <div className={`col-lg-12 col-md-12 col-sm-6 ${isMobile && 'px-0'}`}>
              <Balance
                loading={
                  isLoadingRates ||
                  isLoadingLinkedWallets ||
                  isLoadingCardInfo ||
                  isLoadingCardProvider ||
                  !displayCurrencyRate ||
                  isLoadingCardStatus
                }
                isDepositHidden={isDepositHidden}
                totalChangeInFiat={totalChangeInFiatValue}
                totalBalanceInFiat={cardBalance}
                fiatCurrencyCode={fiatCurrencyCode}
              />
              <div className="d-md-block d-md-none pb-3 px-0">
                <p className="text-f5 text-dark">
                  {isUSVersion
                    ? translate('connect_account_select_order_note_us')
                    : translate('connect_account_select_order_note')}
                </p>
              </div>
            </div>
            <div className={`col-lg-12 col-md-12 col-sm-6 pe-md-2 pe-sm-0 ${isMobile && 'px-0'}`}>
              {isBrowser && renderAddAccount()}
            </div>
          </div>
        </div>
        <div className={`col-lg-9 col-md-8 col-sm-12 ${!isMobile && 'pe-0 ps-sm-0 ps-md-3'}`}>
          <div className="d-flex flex-column px-md-3 px-sm-0">
            <Wallets
              wallets={wallets}
              ledgerAccounts={ledgerAccounts}
              onAddFundsClick={onAddFundsClick}
              fiatCurrencyCode={fiatCurrencyCode}
            />
          </div>
          <div className={`${isMobile ? 'pt-0 pb-3 mb-4' : 'p-2'} px-3`}>
            {isMobile && renderAddAccount()}
          </div>
        </div>
      </div>
      {showLinkWalletsModal && (
        <LinkWalletsModal
          cryptodraftDetails={cardCryptodraftDetails}
          hide={() => setShowLinkWalletsModal(false)}
        />
      )}
      {showDepositModal && (
        <WalletsWithdrawDepositModal
          hide={() => setShowDepositModal(false)}
          wallet={selectedWallet}
          ledgerAccount={selectedLedgerAccount}
          isDeposit
        />
      )}
    </div>
  );
};

export default CardAccounts;
